import { rasterAssetDataUrl } from "@/document/assets/rasterAssets";
import { oklchToCss, parseColor, type OklchColor } from "@/document/model/color";
import { formatHexRgbLabel } from "@/document/model/colorString";
import type { DropShadowEffect, GradientPaint, InnerShadowEffect, NodeEffect, NodeStyle, Paint, StrokeAlign, StrokeStyle } from "@/document/model/styles";
import {
  createBackgroundBlurEffect,
  createDropShadowEffect,
  createInnerShadowEffect,
  createLayerBlurEffect,
  createLinearGradientPaint,
  createSolidPaint,
  normalizeGradientPaint,
  normalizeImagePaint,
  normalizePaint,
  normalizeStrokeStack,
  sampleGradientPaint,
  styleWithStrokeStack,
} from "@/document/model/styles";
import { createId } from "@/lib/ids";
import type { PaintTarget } from "./types";

export type EffectType = NodeEffect["type"];

export function paintForTarget(style: NodeStyle, target: PaintTarget): Paint | undefined {
  const resolved = resolvePaintTarget(target);
  return resolved.type === "fill"
    ? style.fills[resolved.index]
    : normalizeStrokeStack(style)[resolved.index]?.paint;
}

export function styleWithPaint(style: NodeStyle, target: PaintTarget, paint: Paint | OklchColor): NodeStyle {
  const resolved = resolvePaintTarget(target);
  const existing = resolved.type === "fill"
    ? style.fills[resolved.index]
    : normalizeStrokeStack(style)[resolved.index]?.paint;
  const drafted = isPaint(paint) ? paint : createSolidPaint(paint);
  // Keep the stack slot identity when editing an existing fill/stroke paint.
  const nextPaint = existing
    ? { ...normalizePaint(drafted), id: existing.id }
    : normalizePaint(drafted);
  if (resolved.type === "fill") {
    const fills = [...style.fills];
    fills[resolved.index] = nextPaint;
    return {
      ...style,
      fills,
    };
  }

  const strokes = normalizeStrokeStack(style);
  const existingStroke = strokes[resolved.index];
  const stroke: StrokeStyle = {
    paint: nextPaint,
    width: existingStroke?.width ?? 1,
    align: existingStroke?.align ?? "inside",
  };
  const nextStrokes = [...strokes];
  nextStrokes[resolved.index] = stroke;
  return styleWithStrokeStack(style, nextStrokes);
}

export function styleWithAddedFill(style: NodeStyle, paint: Paint = createSolidPaint("#38bdf8")): NodeStyle {
  return {
    ...style,
    fills: [...style.fills, paint],
  };
}

export function styleWithoutFill(style: NodeStyle, index?: number): NodeStyle {
  return {
    ...style,
    fills: index === undefined ? [] : style.fills.filter((_, candidateIndex) => candidateIndex !== index),
  };
}

/** Inserts a deep clone of the fill at `index` immediately above it in the stack (array index + 1). */
export function styleWithDuplicatedFill(style: NodeStyle, index: number): NodeStyle {
  const paint = style.fills[index];
  if (!paint) {
    return style;
  }

  const fills = [...style.fills];
  fills.splice(index + 1, 0, clonePaint(paint));
  return {
    ...style,
    fills,
  };
}

export function clonePaint(paint: Paint): Paint {
  const clone = structuredClone(paint);
  clone.id = createId("paint");
  if (clone.type === "linearGradient" || clone.type === "radialGradient") {
    clone.stops = clone.stops.map(stop => ({
      ...stop,
      id: createId("stop"),
    }));
  }
  return clone;
}

/**
 * Reorders a fill using visual before/after placement. Fill rows are shown with
 * the top-most stack entry first (reversed array order), so placement is resolved
 * in that visual order and then written back to the underlying fills array.
 */
export function styleWithReorderedFill(
  style: NodeStyle,
  fromIndex: number,
  hoverIndex: number,
  placement: "before" | "after" = "before",
): NodeStyle {
  const count = style.fills.length;
  if (
    fromIndex < 0
    || hoverIndex < 0
    || fromIndex >= count
    || hoverIndex >= count
    || fromIndex === hoverIndex
  ) {
    return style;
  }

  const visual = [...style.fills].reverse();
  const fromVisual = count - 1 - fromIndex;
  const hoverVisual = count - 1 - hoverIndex;
  let insertAt = placement === "before" ? hoverVisual : hoverVisual + 1;
  const [fill] = visual.splice(fromVisual, 1);
  if (!fill) {
    return style;
  }
  if (fromVisual < insertAt) {
    insertAt -= 1;
  }
  visual.splice(insertAt, 0, fill);
  return { ...style, fills: visual.reverse() };
}

export function styleWithAddedStroke(style: NodeStyle, paint: Paint = createSolidPaint("#f8fafc")): NodeStyle {
  return styleWithStrokeStack(style, [
    ...normalizeStrokeStack(style),
    {
      paint,
      width: 1,
      align: "inside",
    },
  ]);
}

export function styleWithoutStroke(style: NodeStyle, index?: number): NodeStyle {
  const strokes = normalizeStrokeStack(style);
  return styleWithStrokeStack(style, index === undefined ? [] : strokes.filter((_, candidateIndex) => candidateIndex !== index));
}

export function gradientPaintForTarget(style: NodeStyle, target: PaintTarget): GradientPaint {
  const paint = paintForTarget(style, target);
  if (paint?.type === "linearGradient" || paint?.type === "radialGradient") {
    return paint;
  }

  const baseColor = paint?.type === "solid" ? paint.color : "#000000";
  return createLinearGradientPaint(baseColor, "#ffffff");
}

export function styleWithStrokeWidth(style: NodeStyle, width: number): NodeStyle {
  const strokes = normalizeStrokeStack(style);
  const index = Math.max(0, strokes.length - 1);
  const stroke = strokes[index] ?? { paint: createSolidPaint("#f8fafc"), width: 1, align: "inside" as StrokeAlign };
  const nextStrokes = [...strokes];
  nextStrokes[index] = {
    ...stroke,
    width: Math.max(0, width),
  };
  return styleWithStrokeStack(style, nextStrokes);
}

export function styleWithStrokeAlign(style: NodeStyle, align: StrokeAlign): NodeStyle {
  const strokes = normalizeStrokeStack(style);
  const index = Math.max(0, strokes.length - 1);
  const stroke = strokes[index] ?? { paint: createSolidPaint("#f8fafc"), width: 1 };
  const nextStrokes = [...strokes];
  nextStrokes[index] = {
    ...stroke,
    align,
  };
  return styleWithStrokeStack(style, nextStrokes);
}

export function styleWithStrokeWidthAt(style: NodeStyle, index: number, width: number): NodeStyle {
  const strokes = normalizeStrokeStack(style);
  const stroke = strokes[index];
  if (!stroke) {
    return style;
  }
  const nextStrokes = [...strokes];
  nextStrokes[index] = { ...stroke, width: Math.max(0, width) };
  return styleWithStrokeStack(style, nextStrokes);
}

export function styleWithStrokeAlignAt(style: NodeStyle, index: number, align: StrokeAlign): NodeStyle {
  const strokes = normalizeStrokeStack(style);
  const stroke = strokes[index];
  if (!stroke) {
    return style;
  }
  const nextStrokes = [...strokes];
  nextStrokes[index] = { ...stroke, align };
  return styleWithStrokeStack(style, nextStrokes);
}

export function resolvePaintTarget(target: PaintTarget): { type: "fill" | "stroke"; index: number } {
  return typeof target === "string" ? { type: target, index: 0 } : target;
}

export function opacityForPaint(paint: Paint | undefined): number | undefined {
  if (!paint) {
    return undefined;
  }

  if (paint.type === "solid") {
    return paint.color.alpha * 100;
  }

  if (paint.type === "image") {
    return paint.opacity * 100;
  }

  const maxAlpha = Math.max(0, ...paint.stops.map(stop => stop.color.alpha));
  return maxAlpha * 100;
}

export function paintWithOpacity(paint: Paint | undefined, opacityPercent: number): Paint | undefined {
  if (!paint) {
    return undefined;
  }

  const nextOpacity = Math.max(0, Math.min(1, opacityPercent / 100));
  if (paint.type === "solid") {
    return {
      ...paint,
      color: {
        ...paint.color,
        alpha: nextOpacity,
      },
    };
  }

  if (paint.type === "image") {
    return {
      ...paint,
      opacity: nextOpacity,
    };
  }

  const currentMaxAlpha = Math.max(0, ...paint.stops.map(stop => stop.color.alpha));
  const scale = currentMaxAlpha <= 0 ? 0 : nextOpacity / currentMaxAlpha;
  return {
    ...paint,
    stops: paint.stops.map(stop => ({
      ...stop,
      color: {
        ...stop.color,
        alpha: currentMaxAlpha <= 0 ? nextOpacity : Math.max(0, Math.min(1, stop.color.alpha * scale)),
      },
    })),
  };
}

export function styleWithAddedEffect(style: NodeStyle, type: EffectType): NodeStyle {
  return {
    ...style,
    effects: [...(style.effects ?? []), createEffect(type)],
  };
}

export function styleWithConvertedEffect(style: NodeStyle, effectId: string, type: EffectType): NodeStyle {
  return {
    ...style,
    effects: (style.effects ?? []).map(effect => {
      if (effect.id !== effectId || effect.type === type) {
        return effect;
      }
      return {
        ...createEffect(type),
        id: effect.id,
        visible: effect.visible,
      };
    }),
  };
}

export function styleWithUpdatedEffect(
  style: NodeStyle,
  effectId: string,
  patch: Partial<DropShadowEffect> | Partial<InnerShadowEffect> | Partial<Extract<NodeEffect, { type: "layerBlur" }>> | Partial<Extract<NodeEffect, { type: "backgroundBlur" }>>,
): NodeStyle {
  return {
    ...style,
    effects: (style.effects ?? []).map(effect => effect.id === effectId ? patchEffect(effect, patch) : effect),
  };
}

export function styleWithoutEffect(style: NodeStyle, effectId: string): NodeStyle {
  return {
    ...style,
    effects: (style.effects ?? []).filter(effect => effect.id !== effectId),
  };
}

function normalizeEffect(effect: NodeEffect): NodeEffect {
  if (effect.type === "dropShadow" || effect.type === "innerShadow") {
    return {
      ...effect,
      blur: Math.max(0, effect.blur),
      color: createSolidPaint(effect.color).color,
    };
  }

  if (effect.type === "layerBlur") {
    return {
      ...effect,
      blur: Math.max(0, effect.blur),
      start: Math.max(0, effect.start),
      end: Math.max(0, effect.end),
    };
  }

  return {
    ...effect,
    blur: Math.max(0, effect.blur),
  };
}

function patchEffect(
  effect: NodeEffect,
  patch: Partial<DropShadowEffect> | Partial<InnerShadowEffect> | Partial<Extract<NodeEffect, { type: "layerBlur" }>> | Partial<Extract<NodeEffect, { type: "backgroundBlur" }>>,
): NodeEffect {
  switch (effect.type) {
    case "dropShadow":
      return normalizeEffect({ ...effect, ...patch, type: "dropShadow" } as DropShadowEffect);
    case "innerShadow":
      return normalizeEffect({ ...effect, ...patch, type: "innerShadow" } as InnerShadowEffect);
    case "layerBlur":
      return normalizeEffect({ ...effect, ...patch, type: "layerBlur" } as Extract<NodeEffect, { type: "layerBlur" }>);
    case "backgroundBlur":
      return normalizeEffect({ ...effect, ...patch, type: "backgroundBlur" } as Extract<NodeEffect, { type: "backgroundBlur" }>);
  }
}

function createEffect(type: EffectType): NodeEffect {
  switch (type) {
    case "innerShadow":
      return createInnerShadowEffect();
    case "layerBlur":
      return createLayerBlurEffect();
    case "backgroundBlur":
      return createBackgroundBlurEffect();
    case "dropShadow":
    default:
      return createDropShadowEffect();
  }
}

function isPaint(value: Paint | OklchColor): value is Paint {
  return typeof value === "object" && value !== null && "type" in value;
}

export function paintPreviewBackground(paint: Paint): string {
  if (paint.type === "solid") {
    return oklchToCss(parseColor(paint.color));
  }

  if (paint.type === "image") {
    const source = rasterAssetDataUrl(paint.assetId, paint.source);
    return source ? `url("${source}") center / cover` : "transparent";
  }

  const normalized = normalizeGradientPaint(paint);
  const stops = sampleGradientPaint(normalized, 16)
    .map(stop => `${oklchToCss(stop.color)} ${Math.round(stop.position * 100)}%`)
    .join(", ");
  return normalized.type === "radialGradient"
    ? `radial-gradient(circle, ${stops})`
    : `linear-gradient(${normalized.angle + 90}deg, ${stops})`;
}

export function imagePaintNameLabel(paint: Extract<Paint, { type: "image" }>): string {
  return normalizeImagePaint(paint).name ?? "Image";
}

export function paintValueLabel(paint: Paint): string {
  if (paint.type === "solid") {
    return formatHexRgbLabel(parseColor(paint.color));
  }

  if (paint.type === "image") {
    return imagePaintNameLabel(paint);
  }

  const normalized = normalizeGradientPaint(paint);
  return normalized.type === "radialGradient"
    ? `Radial ${normalized.colorSpace.toUpperCase()}`
    : `${Math.round(normalized.angle)}deg ${normalized.colorSpace.toUpperCase()}`;
}
